import { FC } from "react";
import { SearchWithAutocompleteWrapper } from "../../components/common/SearchWithAutocomplete/SearchWithAutocomplete.styles";
import { t } from "i18next";
import ServerSideSearchBar from "../../components/common/table/ServerSideSearchBar";
import { Stack, Tooltip } from "@mui/material";
import GridOnIcon from '@mui/icons-material/GridOn';
import { usePermissionChecker } from "../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../types/permissionType";
import { useProcessingRequest } from "../../store/selectors/tableManagementSelector";
import { EmployeePageFiltersResponse, EmployeeUserFilterParameters } from "../../types/employeePageFilters";
import { SearchBarButtonContainerStyling } from "../../components/common/table/TableElements.styles";
import BulkDownloadOnboardingFilesButton from "./EmployeesTable/BulkDownloadOnboardingFilesButton";
import CreateBtn from "../../components/common/table/CreateBtn";
import FileUploaderBox from "./EmployeesTable/FileUploaderBox";
import { GeneralButton } from "../../common/ui/GeneralButton";
import { FilterButtonDropDown } from "../../common/ui/FilterButtonDropdown";

const EMPLOYEE_TEMPLATE_URL = "/files/templates/Mitarbeiterliste.xlsx";

interface EmployeesSearchBarProps {
  handleOnSearch: (searchQuery: string) => void;
  onCreateEmployee: () => void;
  filterOptions: EmployeePageFiltersResponse;
  selectedFilters: EmployeeUserFilterParameters;
  onFilterChange: (filter: Partial<EmployeeUserFilterParameters>) => void;
}

const EmployeesSearchBar: FC<EmployeesSearchBarProps> = ({
  handleOnSearch: onSearchHandler,
  onCreateEmployee,
  filterOptions,
  selectedFilters,
  onFilterChange,
}) => {
  const processingRequest = useProcessingRequest();
  const { userHasPermission } = usePermissionChecker();
  const canCreateEmployees = userHasPermission(PermissionEntityType.Employees, "Create");

  const handleDownloadTemplate = (): void => {
    const link = document.createElement('a');
    link.href = EMPLOYEE_TEMPLATE_URL;
    link.setAttribute('download', "Mitarbeiterliste.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const listFilters = [
    { key: "groupIds", label: t("employeesPage.filters.groups"), options: filterOptions.groups },
    { key: "subCompanyIds", label: t("employeesPage.filters.subCompanies"), options: filterOptions.subCompanies },
    { key: "actionModuleIds", label: t("employeesPage.filters.actionModules"), options: filterOptions.actionModules },
    { key: "serviceModuleIds", label: t("employeesPage.filters.serviceModules"), options: filterOptions.serviceModules },
  ];

  return (
    <SearchWithAutocompleteWrapper style={{ filter: processingRequest ? "blur(2px)" : "none", transition: "filter 0.3s ease-in-out" }}>
      <Stack direction="row" spacing={1} style={{ flexWrap: "wrap", rowGap: 1 }}>
        <ServerSideSearchBar onSearchHandler={onSearchHandler} />
        {listFilters.map(({ key, label, options }) => (
          <FilterButtonDropDown
            key={key}
            label={label}
            options={options}
            selectedIds={selectedFilters[key]}
            onToggle={(id) => onFilterChange({
              [key]:
                selectedFilters[key].includes(id)
                  ? selectedFilters[key].filter((x: string) => x !== id)
                  : [...selectedFilters[key], id]
            })}
            onReset={() => onFilterChange({ [key]: [] })}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} style={{ flexWrap: "wrap", rowGap: 1 }}>
        <SearchBarButtonContainerStyling >
          <Tooltip title={t("buttons.templateDownload")}>
            <GeneralButton appVariant="icon" onClick={handleDownloadTemplate}>
              <GridOnIcon />
            </GeneralButton>
          </Tooltip>
          <BulkDownloadOnboardingFilesButton />
          <CreateBtn handleOnClick={canCreateEmployees ? onCreateEmployee : () => { }} adding={!false} />
          <FileUploaderBox />
        </SearchBarButtonContainerStyling>
      </Stack>
    </SearchWithAutocompleteWrapper>
  );
};

export default EmployeesSearchBar;
